import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Container,
  alpha,
  useTheme,
  Chip,
  IconButton,
} from "@mui/material";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { getMyFlashcards, getStudentFlashcards, clearFlashcard, deleteFlashcard } from "../../state/slices/flashcard";
import { useSelector, useDispatch } from "react-redux";
import Loading from "../../components/loading/loading";
import { Edit, Delete, Visibility, School, CheckCircle, Collections, Add, ArrowBack } from "@mui/icons-material";

const MyFlashcards = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const { flashcards, loading, errorMsg } = useSelector((state) => state.flashcard);
  const user = useSelector((state) => state.user.user);
  const isStudentView = location.pathname === '/flashcard/student';
  const isTutor = user?.roles?.includes("TUTOR");
  
  // Root navigation paths - don't show back button on these
  const rootPaths = ['/quiz/my', '/quiz/student', '/flashcard/my', '/flashcard/student', '/progress', '/', '/request', '/student-request', '/tutor-class-room', '/student-class-room'];
  const showBackButton = !rootPaths.includes(location.pathname);

  useEffect(() => {
    if (isStudentView) {
      dispatch(getStudentFlashcards());
    } else {
      dispatch(getMyFlashcards());
    }
  }, [dispatch, isStudentView]);

  const handleDelete = (flashcardId) => {
    if (window.confirm(t('flashcard.delete') + "?")) {
      dispatch(deleteFlashcard(flashcardId)).then(() => {
        if (isStudentView) {
          dispatch(getStudentFlashcards());
        } else {
          dispatch(getMyFlashcards());
        }
      });
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        pb: 4,
      }}
    >
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        {/* Back Button - only show if not on root navigation page */}
        {showBackButton && (
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 3,
              color: 'text.secondary',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            {t('common.back')}
          </Button>
        )}

        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Collections sx={{ fontSize: 32 }} />
              </Box>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    mb: 0.5,
                  }}
                >
                  {isStudentView ? t('flashcard.assignedFlashcards') : t('flashcard.myFlashcards')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isStudentView 
                    ? t('flashcard.assignedFlashcardsDesc') || 'View and study flashcards assigned to you'
                    : t('flashcard.myFlashcardsDesc') || 'Create and manage your flashcard sets'}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate("/flashcard/create")}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: 600,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              {t('flashcard.createNewFlashcard')}
            </Button>
          </Box>
        </Box>

        {flashcards.length === 0 ? (
          <Card
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 3,
              bgcolor: 'white',
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <Collections sx={{ fontSize: 64, color: alpha(theme.palette.primary.main, 0.3), mb: 2 }} />
            <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
              {t('flashcard.noFlashcards')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate("/flashcard/create")}
              sx={{ mt: 2, borderRadius: 2 }}
            >
              {t('flashcard.createNewFlashcard')}
            </Button>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {flashcards.map((flashcard) => (
              <Grid item xs={12} sm={6} md={4} key={flashcard._id}>
                <Card
                  elevation={0}
                  onClick={() => {
                    // Only navigate if view button would be shown
                    if (!isTutor || flashcard.createdBy?._id === user?.id || flashcard.createdBy === user?.id) {
                      navigate(`/flashcard/${flashcard._id}`);
                    }
                  }}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    bgcolor: 'white',
                    transition: 'all 0.3s ease',
                    cursor: (!isTutor || flashcard.createdBy?._id === user?.id || flashcard.createdBy === user?.id) ? 'pointer' : 'default',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          mr: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Collections sx={{ fontSize: 24 }} />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 700,
                            color: theme.palette.primary.main,
                            mb: 0.5,
                            lineHeight: 1.3,
                          }}
                        >
                          {flashcard.title}
                        </Typography>
                        {/* Show creator info for student view */}
                        {isStudentView && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            {flashcard.createdBy?._id === user?.id || flashcard.createdBy === user?.id 
                              ? t('flashcard.createdByMe') 
                              : t('flashcard.createdBy') + ` ${flashcard.createdBy?.firstName || ''} ${flashcard.createdBy?.lastName || ''}`.trim()}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    
                    <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {flashcard.subject && (
                        <Chip 
                          label={flashcard.subject} 
                          size="small" 
                          sx={{ 
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                          }} 
                        />
                      )}
                      {flashcard.gradeLevel && (
                        <Chip 
                          label={flashcard.gradeLevel} 
                          size="small"
                          sx={{ 
                            bgcolor: alpha(theme.palette.secondary.main, 0.1),
                            color: theme.palette.secondary.main,
                            fontWeight: 600,
                          }}
                        />
                      )}
                      {isStudentView && flashcard.isCompleted && (
                        <Chip 
                          icon={<CheckCircle />}
                          label={t('flashcard.completed')} 
                          size="small" 
                          color="success"
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                    </Box>
                    
                    <Box 
                      sx={{ 
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        mb: 2,
                        flexGrow: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 0.5 }}>
                        {flashcard.cards?.length || 0} {t('flashcard.cards')}
                      </Typography>
                      {isStudentView && flashcard.hasProgress && (
                        <Typography variant="body2" color="text.secondary">
                          {t('flashcard.progress')}: {flashcard.masteredCards}/{flashcard.totalCards} {t('flashcard.mastered')}
                        </Typography>
                      )}
                    </Box>
                    
                    <Box 
                      sx={{ display: "flex", gap: 1, justifyContent: 'flex-end' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Only show view button for students/parents, or for tutors if they created it */}
                      {(!isTutor || flashcard.createdBy?._id === user?.id || flashcard.createdBy === user?.id) && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/flashcard/${flashcard._id}`);
                          }}
                          title={t('flashcard.view')}
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.2),
                            },
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      )}
                      {/* Only show edit/delete if user is the creator (tutor) or if it's their own flashcard */}
                      {(isTutor || !isStudentView || flashcard.createdBy?._id === user?.id) && (
                        <>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/flashcard/create`, { state: { flashcard } });
                            }}
                            title={t('flashcard.edit')}
                            sx={{
                              bgcolor: alpha(theme.palette.info.main, 0.1),
                              color: theme.palette.info.main,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.info.main, 0.2),
                              },
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(flashcard._id);
                            }}
                            title={t('flashcard.delete')}
                            sx={{
                              bgcolor: alpha(theme.palette.error.main, 0.1),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.2),
                              },
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default MyFlashcards;

